import { SimplifiedUserRole } from '@platform/client/schemas';
import { Modal } from '@platform/components';
import { useState } from 'react';
import {
  ErrorConfirmationContent,
  SubmittingContent,
  SuccessConfirmationContent,
} from './ConfirmationContent';
import { OtherUpdateContent } from './OtherUpdateContent';
import { ProjectHub } from '@platform/client';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'wouter';
import { useForm, SubmitHandler } from 'react-hook-form';

export enum PlanUpdateModalStage {
  Different_Update = 'different-update',
  NonSuccess_Update_Confirmed = 'nonsuccess-update-confirmed',
  Submitting = 'submitting',
  Error = 'error',
}

export type PlanUpdateFormData = {
  notes: string | null;
};

export const PlanUpdateModalProjectPlan = (props: {
  isOpen: boolean;
  onClose: () => void;
  userType: SimplifiedUserRole;
  isTeam: boolean;
}) => {
  const { matchGroupId } = useParams<{ matchGroupId: string }>();
  const [modalStage, setModalStage] = useState<PlanUpdateModalStage>(
    PlanUpdateModalStage.Different_Update,
  );

  const { handleSubmit, control, formState } = useForm<PlanUpdateFormData>();

  const closeModal = () => {
    setModalStage(PlanUpdateModalStage.Different_Update);
    props.onClose();
  };

  const queryClient = useQueryClient();
  const postPlanUpdateMutator = ProjectHub.usePostPlanUpdate({
    mutation: {
      onMutate: () => {
        setModalStage(PlanUpdateModalStage.Submitting);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ProjectHub.getGetProjectHubDataByMatchGroupQueryKey(parseInt(matchGroupId)),
        });
        setModalStage(PlanUpdateModalStage.NonSuccess_Update_Confirmed);
      },
      onError: () => {
        setModalStage(PlanUpdateModalStage.Error);
      },
    },
  });

  const onSubmit: SubmitHandler<PlanUpdateFormData> = (data) => {
    postPlanUpdateMutator.mutate({
      data: {
        matchGroupId: parseInt(matchGroupId),
        outcome: 'need_help',
        notes: data.notes ?? null,
        isTeam: props.isTeam,
      },
    });
  };

  return (
    <Modal
      isOpen={props.isOpen}
      headerDivision='divider'
      onClose={closeModal}
      title='Planning update'
      variant='full-screen'
      primaryCTA={{
        text: modalStage === PlanUpdateModalStage.Different_Update ? 'Submit' : 'Close',
        callback: () => {
          if (modalStage === PlanUpdateModalStage.Different_Update) {
            handleSubmit(onSubmit)();
          } else {
            closeModal();
          }
        },
      }}
      secondaryCTA={
        modalStage === PlanUpdateModalStage.Different_Update ?
          {
            text: 'Go back',
            callback: () => closeModal(),
          }
        : undefined
      }
    >
      {modalStage === PlanUpdateModalStage.Different_Update && (
        <OtherUpdateContent control={control} formState={formState} name='notes' />
      )}
      {modalStage === PlanUpdateModalStage.Submitting && <SubmittingContent />}
      {modalStage === PlanUpdateModalStage.NonSuccess_Update_Confirmed && (
        <SuccessConfirmationContent>
          Thanks for sharing your update. It helps us better support your project and track your
          progress.
        </SuccessConfirmationContent>
      )}
      {modalStage === PlanUpdateModalStage.Error && <ErrorConfirmationContent />}
    </Modal>
  );
};
